import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Quill from "quill";

import { isRichTextEmpty } from "helpers/isRichTextEmpty";
import cn from "lib/cn";

import "quill/dist/quill.snow.css";
import "./editor.css";

interface EditorProps {
  id?: string;
  value?: string;
  readOnly?: boolean;
  backgroundReadOnlyVariant?: "white" | "gray";
  placeholder?: string;
  onChange?: (content: string) => void;
  onBlur?: (hasValue: boolean) => void;
}

export interface EditorHandle {
  focus: () => void;
}

const Editor = forwardRef<EditorHandle, EditorProps>(
  (
    {
      id,
      value = "",
      readOnly = false,
      placeholder = "",
      backgroundReadOnlyVariant,
      onChange,
      onBlur,
    },
    ref
  ) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<Quill | null>(null);
    const isUserChangeRef = useRef<boolean>(false);
    const lastValueRef = useRef<string>(value);
    const [editorId] = useState(
      `quill-editor-${id || Math.random().toString(36).substring(2, 9)}`
    );

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (editorRef.current) {
          editorRef.current.focus();
        }
        if (quillRef.current) {
          quillRef.current.focus();
          quillRef.current.container.focus();
        }
      },
    }));

    // Completely destroy and recreate the editor container before initialization
    useEffect(() => {
      // Function to completely destroy any existing Quill instances
      const destroyQuill = () => {
        if (quillRef.current) {
          quillRef.current = null;
        }

        // Remove any existing toolbar elements in the document
        document
          .querySelectorAll(`#${editorId} .ql-toolbar`)
          .forEach((toolbar) => {
            toolbar.remove();
          });

        // Recreate editor container
        if (editorRef.current) {
          // Clear the container
          editorRef.current.innerHTML = "";
          // Create a fresh editor div
          const editorElement = document.createElement("div");
          editorElement.id = editorId;
          editorRef.current.appendChild(editorElement);
          return editorElement;
        }
        return null;
      };

      // Create fresh editor container
      const editorElement = destroyQuill();
      if (!editorElement) return;

      const modules = {
        toolbar: readOnly
          ? false
          : [
              [
                "bold",
                "italic",
                "underline",
                "strike",
                { color: [] },
                { list: "ordered" },
                { list: "bullet" },
              ],
            ],
      };

      const formats = [
        "bold",
        "italic",
        "underline",
        "strike",
        "color",
        "list",
      ];

      // Initialize with the fresh container
      quillRef.current = new Quill(editorElement, {
        modules,
        formats,
        theme: "snow",
        placeholder,
        readOnly,
      });

      // Set initial content
      if (value) {
        setQuillContent(quillRef.current, value);
        lastValueRef.current = value;
      }

      quillRef.current.on("text-change", (_delta, _oldDelta, source) => {
        if (source === "user") {
          isUserChangeRef.current = true;
          if (quillRef.current) {
            const html = quillRef.current.root.innerHTML;
            lastValueRef.current = html;
            if (onChange) {
              onChange(html);
            }
          }
          isUserChangeRef.current = false;
        }
      });

      quillRef.current.on("selection-change", (range, _oldRange, _source) => {
        if (range === null) {
          if (onBlur) {
            const html = quillRef.current?.root.innerHTML || "";
            const isEmpty = isRichTextEmpty(html);
            onBlur(!isEmpty);
          }
        }
      });

      if (ref) {
        if (typeof ref === "function") {
          ref(quillRef.current);
        } else {
          ref.current = quillRef.current;
        }
      }

      return () => {
        destroyQuill();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorId, readOnly, placeholder]); // Only depend on the stable editorId

    // Handle value prop changes
    useEffect(() => {
      if (
        quillRef.current &&
        value !== lastValueRef.current &&
        !isUserChangeRef.current
      ) {
        setQuillContent(quillRef.current, value);
        lastValueRef.current = value;
      }
    }, [value]);

    // Update readOnly state if it changes
    useEffect(() => {
      if (quillRef.current) {
        quillRef.current.enable(!readOnly);
      }
    }, [readOnly]);

    const setQuillContent = (quill: Quill, content: string) => {
      try {
        const parsedContent =
          typeof content === "string" && content.trim().startsWith("{")
            ? JSON.parse(content)
            : content;

        if (typeof parsedContent === "object" && parsedContent.ops) {
          quill.setContents(parsedContent);
        } else {
          quill.root.innerHTML = content;
        }
      } catch (e) {
        console.error("Error parsing Quill content:", e);
        quill.root.innerHTML = content;
      }
    };

    return (
      <div
        className={cn(
          "custom-editor-container",
          readOnly &&
            (backgroundReadOnlyVariant === "gray"
              ? "readonly-gray"
              : "readonly-white")
        )}
      >
        <div ref={editorRef} className="quill-editor-container" />
      </div>
    );
  }
);

Editor.displayName = "Editor";

export default Editor;
